// Google Analytics 4 (a Firebase webapp GA4-adatfolyama), a süti-kapun át,
// Google Consent Mode v2-vel.
//
// MIÉRT gtag.js, ÉS NEM Firebase Analytics. A `firebase/analytics` is gtag.js-t
// tölt be, de saját maga küld oldalmegtekintést, saját IndexedDB/Installations
// forgalmat indít, és a betöltését nem a mi kapunk vezérli. A Meta pixel
// mintájára itt mi döntünk arról, hogy MIKOR töltődik be — és pontosan egy
// helyen keletkezik `page_view`.
//
// KAPUZÁS. A gtag.js CSAK „Statisztika" hozzájárulás után töltődik be
// (`kapuzva('statisztika', …)`): hozzájárulás nélkül semmilyen Google-kód nem
// fut, és nem küld cookie nélküli pingeket sem. Betöltéskor a Consent Mode
// alapértéke MINDEN jelre `denied`, és csak ezután, a `config` ELŐTT frissül a
// tényleges döntés szerint:
//   statisztika -> analytics_storage
//   marketing   -> ad_storage, ad_user_data, ad_personalization
//
// VISSZAVONÁS. A „Süti-beállítások" felületen a látogató bármikor
// visszavonhatja; ilyenkor a Consent Mode `denied`-re vált, a GA hivatalos
// kikapcsoló kapcsolója (`ga-disable-<ID>`) bekapcsol, és a `_ga` sütiket
// töröljük. Újbóli megadáskor a mérés újraindul — oldalfrissítés nélkül.
//
// AZONOSÍTÓ. Nem titok (a böngészőben amúgy is látszik). Egyetlen helyen él:
// a `VITE_GA_MEASUREMENT_ID` felülírhatja, alapértéke az alábbi; `off`
// értékkel kikapcsolható. Fejlesztői szerveren csak akkor mér, ha a változó
// kifejezetten be van állítva — különben a helyi kattintgatás az éles
// statisztikába kerülne.

import { aktualisAllapot, figyelDontest, kapuzva, type SutiAllapot } from './consent';

export const ALAP_MERESI_AZONOSITO = 'G-0GRWRDLG9M';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    [kikapcsolo: `ga-disable-${string}`]: boolean | undefined;
  }
}

type GaAllapot = {
  azonosito: string;
  tarolo: Storage | null | undefined;
  betoltve: boolean;
  utolsoOldal: string | null;
};

let allapot: GaAllapot | null = null;

/// A mérési azonosító a buildkörnyezetből, vagy `null`, ha a mérés ki van kapcsolva.
///
/// Az `import.meta.env` a Vite alatt létezik; a böngésző nélküli mérőszkriptben
/// (tsx) nem — ezért az óvatos hozzáférés.
export function meresiAzonosito(
  env: Record<string, unknown> | undefined = typeof import.meta.env === 'undefined'
    ? undefined
    : (import.meta.env as Record<string, unknown>),
): string | null {
  const nyers = typeof env?.VITE_GA_MEASUREMENT_ID === 'string' ? env.VITE_GA_MEASUREMENT_ID.trim() : '';
  if (nyers.toLowerCase() === 'off') return null;
  if (nyers) return nyers;
  // Fejlesztői szerveren kifejezett beállítás nélkül nem mérünk.
  if (env?.DEV === true) return null;
  return ALAP_MERESI_AZONOSITO;
}

/// A hozzájárulás Consent Mode v2 alakban.
export function consentJelek(a: SutiAllapot): Record<string, 'granted' | 'denied'> {
  const s = (be: boolean): 'granted' | 'denied' => (be ? 'granted' : 'denied');
  return {
    analytics_storage: s(a.statisztika),
    ad_storage: s(a.marketing),
    ad_user_data: s(a.marketing),
    ad_personalization: s(a.marketing),
  };
}

function gtag(...args: unknown[]): void {
  try {
    window.gtag?.(...args);
  } catch (error) {
    console.warn('[BlazeData] GA-hívás sikertelen.', error);
  }
}

function statisztikaEngedelyezett(): boolean {
  return allapot !== null && aktualisAllapot(allapot.tarolo).statisztika;
}

/// Oldalmegtekintés a jelenlegi (hash-alapú) útvonalra.
///
/// Az automatikus `page_view` ki van kapcsolva (`send_page_view: false`), így
/// ez az EGYETLEN forrás — nincs dupla számolás. Ugyanarra a címre egymás után
/// nem küld kétszer.
/// A GA-nak küldött `page_location`: a hash-útvonal VALÓDI útvonalként.
///
/// A GA4 `pagePath` dimenziója eldobja a fragmentumot, így a teljes cím
/// (`…/#/regisztracio`) minden képernyőt „/"-ként jelentene. Ezért:
///   https://app.blazedata.hu/#/regisztracio -> https://app.blazedata.hu/regisztracio
///   `#/` vagy üres hash                    -> https://app.blazedata.hu/
///
/// LEKÉRDEZÉS: csak a kampány-paraméterek maradnak (`utm_*`, `gclid`,
/// `gbraid`, `wbraid`) — a valódi query-ből és a hash-útvonal query-jéből is.
/// Minden más (azonosítók, tokenek, `tipus=…`) kimarad, hogy semmi ne
/// szivárogjon a GA-ba.
export function gaOldalCim(href: string): string {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return href;
  }
  const hash = u.hash.replace(/^#/, '');
  const [hashUt = '', hashQuery = ''] = hash.split('?', 2);
  let ut = hash ? hashUt : u.pathname;
  if (!ut.startsWith('/')) ut = `/${ut}`;
  ut = ut.replace(/\/{2,}/g, '/');
  if (ut.length > 1) ut = ut.replace(/\/+$/, '');

  const megtartott = new URLSearchParams();
  const kampany = (k: string): boolean =>
    k.startsWith('utm_') || k === 'gclid' || k === 'gbraid' || k === 'wbraid';
  for (const forras of [new URLSearchParams(u.search), new URLSearchParams(hashQuery)]) {
    forras.forEach((v, k) => {
      if (kampany(k) && !megtartott.has(k)) megtartott.set(k, v);
    });
  }
  const q = megtartott.toString();
  return `${u.origin}${ut}${q ? `?${q}` : ''}`;
}

export function gaOldalMegtekintes(): boolean {
  if (!allapot?.betoltve || !statisztikaEngedelyezett()) return false;
  const cim = gaOldalCim(window.location.href);
  if (cim === allapot.utolsoOldal) return false;
  allapot.utolsoOldal = cim;
  // `set`: a képernyőn ezután küldött MINDEN esemény (pl. `sign_up`) is ezt a
  // címet viszi, nem a gtag által látott, hash-es nyers címet.
  gtag('set', { page_location: cim });
  gtag('event', 'page_view', {
    page_location: cim,
    page_title: document.title,
  });
  return true;
}

function betolt(): void {
  if (!allapot || allapot.betoltve || typeof window === 'undefined') return;
  const id = allapot.azonosito;
  allapot.betoltve = true;
  window[`ga-disable-${id}`] = false;

  window.dataLayer = window.dataLayer ?? [];
  // A gtag.js az `arguments` OBJEKTUMOT várja a dataLayerben, nem tömböt —
  // ezért a hivatalos betöltő mintája szerint `function`, nem nyílfüggvény.
  window.gtag =
    window.gtag ??
    function gtagBetolto() {
      window.dataLayer!.push(arguments);
    };

  // SORREND: alapérték (minden tiltva) -> tényleges döntés -> config.
  gtag('consent', 'default', consentJelek({ statisztika: false, marketing: false }));
  gtag('consent', 'update', consentJelek(aktualisAllapot(allapot.tarolo)));
  gtag('set', 'ads_data_redaction', true);
  gtag('js', new Date());
  gtag('config', id, { send_page_view: false });

  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  } catch (error) {
    console.warn('[BlazeData] A gtag.js nem töltődött be.', error);
  }

  gaOldalMegtekintes();
  // Hash-alapú útválasztás: minden képernyőváltás külön oldalmegtekintés. A
  // késleltetés azért kell, hogy a router addigra beállítsa az új címet
  // (`document.title`) — az ő figyelője ugyanerre az eseményre fut.
  window.addEventListener('hashchange', () => {
    setTimeout(gaOldalMegtekintes, 0);
  });
}

/// A `_ga` és `_ga_<ID>` sütik törlése visszavonáskor — minden szóba jövő
/// domainszinten, mert a gtag a legfelső beállítható domainre (pl.
/// `.blazedata.hu`) teszi őket.
function torolGaSutiket(id: string): void {
  try {
    const nevek = ['_ga', `_ga_${id.replace(/^G-/, '')}`];
    const reszek = window.location.hostname.split('.');
    const domainek: (string | null)[] = [null];
    for (let i = 0; i < reszek.length - 1; i += 1) {
      domainek.push(`.${reszek.slice(i).join('.')}`);
    }
    for (const nev of nevek) {
      for (const d of domainek) {
        document.cookie = `${nev}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`;
      }
    }
  } catch {
    // Süti-hozzáférés nélkül nincs mit törölni.
  }
}

function dontesValtozott(a: SutiAllapot): void {
  if (!allapot?.betoltve) return; // a betöltést a kapu intézi
  const id = allapot.azonosito;
  gtag('consent', 'update', consentJelek(a));
  window[`ga-disable-${id}`] = !a.statisztika;
  if (!a.statisztika) {
    torolGaSutiket(id);
    allapot.utolsoOldal = null;
  } else {
    gaOldalMegtekintes();
  }
}

/// A GA bekötése. Egyszer kell hívni, a süti-banner megjelenítése ELŐTT (a
/// banner ebből is tudja, hogy van mire hozzájárulást kérni).
export function gaBekotes(opciok?: { azonosito?: string | null; tarolo?: Storage | null }): void {
  if (allapot) return;
  const azonosito = opciok && 'azonosito' in opciok ? opciok.azonosito : meresiAzonosito();
  if (!azonosito) return;
  allapot = { azonosito, tarolo: opciok?.tarolo, betoltve: false, utolsoOldal: null };
  figyelDontest(dontesValtozott);
  kapuzva('statisztika', betolt, opciok?.tarolo);
}

/// GA4-esemény (pl. `sign_up`). Hozzájárulás vagy betöltött GA nélkül nem
/// csinál semmit, és SOHA nem dob kivételt. Személyes adatot ne adj át.
export function gaEsemeny(nev: string, parameterek?: Record<string, unknown>): boolean {
  if (typeof window === 'undefined' || !allapot?.betoltve || !statisztikaEngedelyezett()) {
    return false;
  }
  gtag('event', nev, {
    page_location: gaOldalCim(window.location.href),
    ...(parameterek ?? {}),
  });
  return true;
}

/// Csak a méréshez: a modul állapotának ürítése két eset között.
export function torolGaAllapotot(): void {
  allapot = null;
}
